import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SOCIALS } from "../data/content";
import {
  Sparkles,
  ArrowRight,
  Compass,
  Crown,
  ArrowDown,
  LayoutGrid,
  PenLine,
} from "lucide-react";
import GlobalSearchBar from "./GlobalSearchBar";
import AppStoreLink from "./AppStoreLink";
import PlayStoreLink from "./PlayStoreLink";
import MacAppBadge from "./MacAppBadge";
import HappyHourLiveBanner from "./HappyHourLiveBanner";

const API = `${window.__BACKEND__}/api`;

const SOCIAL_IMAGES = {
  instagram: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/ssux65x8_Screenshot%202026-06-23%20alle%2016.35.01.png",
  facebook: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/fkivkxu8_Screenshot%202026-06-23%20alle%2016.34.53.png",
  youtube: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/rxme82np_Screenshot%202026-06-23%20alle%2016.34.38.png",
  linkedin: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/ib0c17e7_Screenshot%202026-06-23%20alle%2016.34.32.png",
  tiktok: "https://customer-assets.emergentagent.com/job_vita-portal/artifacts/70g0ioyr_Screenshot%202026-06-23%20alle%2016.34.46.png",
};

// Official Threads glyph (monochrome) — rendered on a black disc to match the brand.
function ThreadsGlyph({ className = "" }) {
  return (
    <svg viewBox="0 0 192 192" className={className} fill="currentColor" aria-hidden="true">
      <path d="M141.537 88.988c-.827-.396-1.667-.778-2.518-1.143-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.694 14.724-10.548 21.347-10.548h.23c8.25.053 14.475 2.451 18.504 7.129 2.932 3.405 4.893 8.111 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.141-23.82 1.372-39.134 15.265-38.105 34.569.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.809-.169-40.06-7.484-51.275-21.742C29.236 139.966 23.808 120.682 23.605 96c.203-24.682 5.631-43.966 16.134-57.317C50.954 24.425 68.204 17.11 91.013 16.94c22.975.171 40.526 7.521 52.171 21.848 5.71 7.026 10.015 15.861 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C141.036 9.607 119.202.195 91.07 0h-.113C62.882.194 41.292 9.642 26.788 28.079 13.882 44.486 7.224 67.316 7.001 95.933L7 96l.001.067c.223 28.617 6.881 51.447 19.787 67.854C41.292 182.358 62.882 191.806 90.957 192h.113c24.96-.173 42.554-6.708 57.048-21.192 18.963-18.943 18.392-42.693 12.142-57.266-4.484-10.455-13.033-18.946-24.723-24.554zM98.44 129.507c-10.44.588-21.286-4.098-21.82-14.135-.397-7.442 5.296-15.746 22.461-16.735 1.966-.114 3.895-.169 5.79-.169 6.235 0 12.068.606 17.371 1.765-1.978 24.702-13.58 28.713-23.802 29.274z"/>
    </svg>
  );
}

// Compact follower-count formatter: 50600 → "50.6K", 12700 → "12.7K", 500 → "500"
function fmtCount(n) {
  if (n == null || isNaN(n)) return "";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1).replace(".0", "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1).replace(".0", "") + "K";
  return String(n);
}

export function HeroSocials() {
  const [counts, setCounts] = useState(null);
  const [displayTotal, setDisplayTotal] = useState(0);

  useEffect(() => {
    let aborted = false;
    fetch(`${API}/social-followers`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!aborted && data) setCounts(data); })
      .catch(() => {});
    return () => { aborted = true; };
  }, []);

  // Determine the social with most followers → gets the LIVE green pulsing ring
  const liveIcon = (() => {
    if (!counts) return null;
    let max = -1;
    let winner = null;
    for (const s of SOCIALS) {
      const c = counts[s.icon];
      if (typeof c === "number" && c > max) {
        max = c;
        winner = s.icon;
      }
    }
    return winner;
  })();

  // Somma follower di TUTTE le piattaforme (aggiornata dai numeri admin)
  const total = counts
    ? Object.values(counts).reduce((a, v) => a + (typeof v === "number" ? v : 0), 0)
    : 0;

  // Conteggio animato all'apertura della home (0 → total, ease-out)
  useEffect(() => {
    if (total <= 0) return;
    let raf;
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayTotal(Math.round(total * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplayTotal(total);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.6 }}
      className="relative z-10 flex flex-wrap gap-x-3 gap-y-2 justify-center mb-3"
      data-testid="hero-socials"
    >
      {SOCIALS.map((s) => {
        const imgSrc = SOCIAL_IMAGES[s.icon];
        const count = counts ? counts[s.icon] : null;
        const isLive = liveIcon === s.icon;
        return (
          <a
            key={s.name}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${s.name}${count ? ` · ${count} follower` : ""}${isLive ? " · LIVE" : ""}`}
            data-testid={`hero-social-${s.icon}`}
            className="group relative flex flex-col items-center gap-1"
          >
            <span className={`relative w-11 h-11 rounded-full overflow-hidden bg-white/5 ${s.icon === "threads" ? "ring-2 ring-white/60" : "ring-1 ring-white/10"} group-hover:ring-[var(--vdh-pink)] group-hover:scale-110 transition-all duration-300 flex items-center justify-center`}>
              {s.icon === "threads" ? (
                <span className="w-full h-full flex items-center justify-center bg-black">
                  <ThreadsGlyph className="w-6 h-6 text-white" />
                </span>
              ) : imgSrc ? (
                <img
                  src={imgSrc}
                  alt={s.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : null}
            </span>
            {count != null && count > 0 && (
              <span
                className="text-xs font-bold tabular-nums tracking-wide text-white"
                data-testid={`hero-social-count-${s.icon}`}
              >
                {fmtCount(count)}
              </span>
            )}
          </a>
        );
      })}
    </motion.div>
    {total > 0 && (
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="relative z-10 text-center text-white/60 text-sm md:text-base mb-6"
        data-testid="hero-community-total"
      >
        Siamo{" "}
        <span
          className="font-display font-black neon-pink text-2xl md:text-3xl tabular-nums"
          data-testid="hero-community-count"
        >
          {displayTotal.toLocaleString("it-IT")}
        </span>{" "}
        nella community
      </motion.p>
    )}
    </>
  );
}

export default function Hero({ onQuizOpen }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[72vh] md:min-h-[68vh] flex flex-col items-center justify-center vdh-top-offset pb-10 px-5 vdh-hero-glow"
    >
      {/* CSS-painted starfield — single composited layer (36 separate motion
          spans were burning ~10ms/frame on integrated GPUs). */}
      <div aria-hidden className="vdh-starfield" />

      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        className="relative z-10 font-display text-4xl sm:text-5xl lg:text-6xl text-center max-w-5xl leading-[1.04] tracking-tight"
        data-testid="hero-tagline"
      >
        Un ecosistema <span className="neon-pink">completo</span>
        <br />
        per chi vive di <span className="neon-yellow">ospitalità</span>.
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55, duration: 0.7 }}
        className="relative z-10 mt-5 max-w-2xl text-center text-sm md:text-base text-white/60"
      >
        <strong className="text-white font-semibold">
          Il portale italiano per host e property manager.
        </strong>{" "}
        Corsi, consulenze, strumenti e app: tutto il mondo degli affitti
        brevi, in un unico posto. Senza fuffa, con professionisti veri.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.7 }}
        className="relative z-[70] mt-7 w-full px-4 sm:px-0"
      >
        <GlobalSearchBar />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.95, duration: 0.7 }}
        className="relative z-10 mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center flex-wrap"
      >
        {/* CTA 1 — Scrivi la Legge (bianco pieno, penna) · pulse */}
        <div className="relative inline-block">
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
            <Link
              to="/leggehost"
              data-testid="hero-cta-leggehost"
              className="relative inline-flex items-center gap-2.5 px-6 py-4 rounded-full font-bold text-sm md:text-base text-black overflow-hidden group w-[260px] justify-center"
              style={{
                background: "#ffffff",
              }}
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity vdh-shimmer-on-hover"
                style={{
                  background:
                    "linear-gradient(120deg, transparent 30%, rgba(255,79,207,0.25) 50%, transparent 70%)",
                }}
              />
              <PenLine size={16} />
              Scrivi la Legge
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>

        {/* CTA 2 — Esplora servizi (gradient giallo→verde) */}
        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} className="relative inline-block">
          <Link
            to="/servizi"
            data-testid="hero-cta-services"
            className="relative inline-flex items-center gap-2.5 px-6 py-4 rounded-full font-semibold text-sm md:text-base text-black overflow-hidden group w-[260px] justify-center"
            style={{
              background: "linear-gradient(135deg, #EA580C 0%, #EA580C 50%, #4fffb0 100%)",
            }}
          >
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity vdh-shimmer-on-hover"
              style={{
                background:
                  "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)",
              }}
            />
            <Compass size={16} />
            Esplora i servizi
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* CTA 3 — Consulenze esclusive (rosso + pulse + badge TOP) */}
        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} className="relative inline-block">
          <span
            className="absolute -top-2 -right-2 z-10 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-tech tracking-[0.18em] uppercase font-bold text-black"
            style={{
              background: "linear-gradient(90deg, #EA580C, #fff)",
              boxShadow: "0 0 12px rgba(234,88,12,0.85)",
            }}
            data-testid="hero-cta-consult-top-badge"
          >
            Top
          </span>
          <Link
            to="/consulenze"
            data-testid="hero-cta-consult"
            className="relative inline-flex items-center gap-2.5 px-6 py-4 rounded-full font-semibold text-sm md:text-base text-white overflow-hidden group min-w-[260px] justify-center whitespace-nowrap"
            style={{
              background: "linear-gradient(135deg, #ff3b3b 0%, #ff5151 55%, #ffb14f 100%)",
            }}
          >
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity vdh-shimmer-on-hover"
              style={{
                background:
                  "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.30) 50%, transparent 70%)",
              }}
            />
            <Crown size={16} />
            Consulenze esclusive
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </motion.div>

      {/* HappyHour LIVE banner — appears only during active event window */}
      <HappyHourLiveBanner />

      {/* === DOWNLOAD OFFICIAL APPS — 3 elegant slim buttons === */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15, duration: 0.6 }}
        className="relative z-10 mt-6 md:mt-7 flex flex-col items-center gap-2.5"
        data-testid="hero-download-apps"
      >
        <div className="flex items-center gap-1.5 text-[11px] font-tech uppercase tracking-[0.30em] text-white/55">
          Scarica l'app ufficiale
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex"
          >
            <ArrowDown size={12} className="text-[var(--vdh-yellow)]" />
          </motion.span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 items-center justify-center w-full sm:w-auto">
          <AppStoreLink testid="hero-app-appstore" compact className="w-[200px] sm:w-auto" />
          <PlayStoreLink testid="hero-app-android" compact className="w-[200px] sm:w-auto" />
          <MacAppBadge testid="hero-app-desktop" compact className="w-[200px] sm:w-auto" />
        </div>
      </motion.div>
    </section>
  );
}
